import { useState, useEffect, useRef } from 'react';
import { BRRApiClient } from '../services/api';
import { PackagedProduct } from '../services/types';

interface UsePackagedProductsOptions {
  apiClient: BRRApiClient;
  pharmaceuticalProductName: string | null;
  enabled: boolean;
}

interface UsePackagedProductsReturn {
  data: PackagedProduct[] | null;
  loading: boolean;
  error: Error | null;
}

export const usePackagedProducts = ({
  apiClient,
  pharmaceuticalProductName,
  enabled
}: UsePackagedProductsOptions): UsePackagedProductsReturn => {
  const [data, setData] = useState<PackagedProduct[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!pharmaceuticalProductName || !enabled) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    const currentRequestId = ++requestIdRef.current;

    const fetchProducts = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.getPackagedProducts(pharmaceuticalProductName);

        if (currentRequestId === requestIdRef.current) {
          setData(response.products);
        }
      } catch (err) {
        if (currentRequestId === requestIdRef.current) {
          const message = err instanceof Error ? err.message : String(err);
          if (message.toLowerCase().includes('not found') || message.includes('404')) {
            setData([]);
          } else {
            setError(err instanceof Error ? err : new Error(message));
            setData(null);
          }
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    void fetchProducts();
  }, [apiClient, pharmaceuticalProductName, enabled]);

  return { data, loading, error };
};
